import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Calendar, Building, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { QuotationForm } from "../components/QuotationForm";
import { WHATSAPP_URL } from "../lib/i18n";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-slate-500">Loading...</div>;
  if (!project) return <div className="py-32 text-center text-slate-500">Project not found. <Link to="/projects" className="text-[#0891b2] underline">Back to projects</Link></div>;

  const title = lang === "ar" ? project.title_ar : project.title_en;
  const desc = lang === "ar" ? project.description_ar : project.description_en;
  const scope = lang === "ar" ? project.scope_ar : project.scope_en;
  const equipment = lang === "ar" ? project.equipment_ar : project.equipment_en;
  const location = lang === "ar" ? project.location_ar : project.location_en;

  return (
    <div data-testid="project-detail-page">
      {/* Hero */}
      <section className="relative aspect-[21/9] min-h-[380px] overflow-hidden">
        <img src={project.image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-10">
            <Link to="/projects" data-testid="project-back" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4">
              <Arrow className="w-4 h-4 rotate-180" /> {t.projects.backToAll}
            </Link>
            <div className="text-[11px] uppercase tracking-widest text-red-200 mb-2 font-semibold">{t.categories[project.category]}</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-4xl" data-testid="project-title">{title}</h1>
          </div>
        </div>
      </section>

      {/* Meta */}
      <section className="border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 py-8">
          {project.client_name && (
            <MetaItem icon={Building} label={t.projects.client} value={project.client_name} />
          )}
          {location && <MetaItem icon={MapPin} label={t.projects.location} value={location} />}
          {project.emirate && <MetaItem icon={MapPin} label={t.projects.emirate} value={t.emirates[project.emirate]} />}
          {project.completion_date && <MetaItem icon={Calendar} label={t.projects.completed} value={project.completion_date} />}
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <p className="text-slate-700 text-lg leading-relaxed">{desc}</p>
            </div>
            {scope && (
              <div>
                <h2 className="font-display text-xl text-[#0891b2] mb-3">{t.projects.scope}</h2>
                <p className="text-slate-700 leading-relaxed">{scope}</p>
              </div>
            )}
            {equipment && (
              <div>
                <h2 className="font-display text-xl text-[#0891b2] mb-3">{t.projects.equipment}</h2>
                <p className="text-slate-700 leading-relaxed">{equipment}</p>
              </div>
            )}
            {project.brands_used?.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-[#0891b2] mb-3">{t.projects.brandsUsed}</h2>
                <div className="flex flex-wrap gap-2">
                  {project.brands_used.map((b, i) => (
                    <span key={i} className="px-3 py-1.5 bg-slate-100 text-slate-800 rounded text-sm font-medium">{b}</span>
                  ))}
                </div>
              </div>
            )}
            {project.gallery?.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-[#0891b2] mb-3">{t.projects.gallery}</h2>
                <div className="grid grid-cols-2 gap-4">
                  {project.gallery.map((g, i) => (
                    <img key={i} src={g} alt={`gallery-${i}`} className="w-full aspect-[4/3] object-cover rounded" />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar CTA */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="bg-[#0891b2] text-white p-6 rounded-lg mb-4">
              <h3 className="font-display text-xl mb-3">{t.projects.similar}</h3>
              <p className="text-cyan-100 text-sm mb-5 leading-relaxed">Get a customized quotation based on this project's specifications.</p>
              <Button
                onClick={() => setShowForm(!showForm)}
                data-testid="request-similar-btn"
                className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white font-semibold h-11"
              >
                {t.projects.similar}
              </Button>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold h-11 mt-3">
                  <MessageCircle className="w-4 h-4 me-2" /> WhatsApp
                </Button>
              </a>
            </div>
          </aside>
        </div>

        {showForm && (
          <div className="max-w-3xl mx-auto px-6 lg:px-10 mt-10 pt-10 border-t border-slate-200" data-testid="similar-form">
            <h2 className="font-display text-2xl text-[#0891b2] mb-6">{t.projects.similar}</h2>
            <QuotationForm projectReference={title} />
          </div>
        )}
      </section>
    </div>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 bg-cyan-50 rounded flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#0891b2]" />
      </div>
      <div>
        <div className="text-[11px] uppercase text-slate-500 tracking-widest">{label}</div>
        <div className="text-sm font-medium text-slate-900">{value}</div>
      </div>
    </div>
  );
}
